/* eslint-disable @typescript-eslint/ban-ts-comment */
import { FC, useMemo } from "react";
import { useSelector } from "react-redux";
import { Redirect, useLocation } from "react-router-dom";

import DomesticTourismForm, {
  DOMESTIC_TOURISM_FORM_VARIANTS,
} from "web/Pages/Tourism/DomesticTourismForm";
import { names } from "web/Pages/Tourism/DomesticTourismForm/domesticTourismFormFields";

import CampaignGraphicsProvider from "web/Layout/CampaignGraphics";
import ErrorComponent from "web/Layout/Common/ErrorComponent";
import {
  SkeletonCategoriesListing,
  SkeletonProductsList,
} from "web/Layout/Common/SkeletonComponents";
import ErrorBoundary from "web/Layout/ErrorBoundary";
import ListingHeader from "web/Layout/Listing/Common/Header";
import Products from "web/Layout/Listing/Common/Products";
import classes from "web/Layout/Listing/listing.scss";
import classesMobile from "web/Layout/Listing/listingMobile.scss";

import Filters from "web/Components/Common/Filters";
import Map from "web/Components/Common/Filters/Desktop/Map";

import {
  useDataCachedAttributes,
  useDataCachedProductsTypes,
} from "web/hooks/useDataCached";

import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";
import getProductTypeFilter from "web/utils/page/category/getProductTypeFilter";
import isAttributeHasAnixeTourismOption from "web/utils/page/category/isAttributeHasAnixeTourismOption";

import placements from "web/constants/campaignGraphics";
import {
  sortOptions as originalSortOptions,
  pageDefault,
  pageName,
  sortCategoryAnixeOptions,
  sortCategoryOptions,
} from "web/constants/toolbar";
import urls from "web/constants/urls";

import { IAttribute } from "web/types/Attributes";
import type { Facets } from "web/types/Facets";
import { IProduct } from "web/types/Product";
import type { Stats } from "web/types/Stats";
import { ISubscriptionFilterBenefitGroups } from "web/types/Subscription";

import { useAppContext } from "web/context/app";

import useBenefitsListingSearch from "./useBenefitsListingSearch";

const facetsHidden = ["mb_product_type_filter"];
const facetsHiddenMobile = ["mb_product_type_filter", "category_ids"];
const facetsHiddenForAnixeTourism = [
  "mb_product_type_filter",
  "category_ids",
  "geolocation_data",
];
interface IListingSearchProps {
  ids: number[];
  facets: Facets;
  stats: Stats;
  name: string;
  count: number;
  additionalParameters: string;
  categoriesPath: string;
  cgGroup1Path?: string;
  subscriptionItems?: { id: string; sku: string }[];
}

const ListingSearch: FC<IListingSearchProps> = ({
  ids,
  facets,
  stats,
  name,
  count,
  additionalParameters,
  categoriesPath,
  cgGroup1Path,
  subscriptionItems = [],
}) => {
  const {
    token,
    id: storeId,
    grid_per_page: gridPerPage,
    can_show_anixe_search: canShowAnixeSearch,
  } = useSelector((state) => state.app.storeConfig);
  const { isMobile } = useAppContext();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const locationParams = params.get(names.location);
  const phrase = params.get("search");

  const options = useMemo(() => {
    const filterCodes = [
      ...(stats?.map((stat) => stat.code) || []),
      ...(facets?.map((facet) => facet.code) || []),
    ];
    const filterFilteredCodes = filterCodes.filter(
      (item) => item && ["price"].indexOf(item) === -1
    );

    return {
      ids: filterFilteredCodes,
      storeId,
      token,
    };
  }, [storeId, token, stats, facets]);

  const { loading, error, data } = useDataCachedAttributes(options);

  const {
    loading: isLoadingFilteredSub,
    idsMapped,
    skuFilteredBenefits: filteredSubSkuArray,
    idsValidSubscriptions,
    dataFiltered,
  } = useBenefitsListingSearch(ids, subscriptionItems);

  const idsMemo = useMemo(() => {
    const pageSizeDefault = gridPerPage;
    const pageCurrent = parseInt(
      params.get(pageName) || pageDefault.toString(),
      10
    );
    const pageSizeCurrent = pageCurrent * pageSizeDefault;
    return ids?.slice(0, pageSizeCurrent);
  }, [gridPerPage, params, pageName, pageDefault]);

  const mbProductTypeFilter = useMemo(
    () => getProductTypeFilter(search),
    [search]
  );

  const productTypeFromFilter = mbProductTypeFilter?.split(
    "mb_product_type_filter="
  )[1];

  // @ts-ignore
  const isSingleProductType = productTypeFromFilter?.length <= 2;

  const { data: productsLoaded, loading: productsLoading } =
    useDataCachedProductsTypes({
      ids: idsMemo?.slice(0, gridPerPage),
      token,
      storeId,
      /**
       * we skip this request
       * if mb product type filter is active
       * and set to one product type
       */
      skip: isSingleProductType,
    });

  switch (true) {
    case !!error: {
      return <ErrorComponent />;
    }
    case !!loading || !data || data.length === 0: {
      return <SkeletonCategoriesListing />;
    }
    default: {
      const isAnixeTourism = isAttributeHasAnixeTourismOption({
        attributes: data,
        facets,
      });

      // Redirect to Tourism LP from main search
      if (canShowAnixeSearch && isAnixeTourism) {
        return (
          <Redirect to={`${urls.tourism}?${names.tourismObject}=${phrase}`} />
        );
      }

      /**
       * if product type request is skipped
       * we still have to maintain our data shape which is
       * [{id: string, mb_product_type: string}]
       */
      const products =
        productsLoaded ??
        getProductsWithType({
          attributes: data,
          // @ts-ignore
          productTypeFromFilter,
          ids: idsMemo?.slice(0, gridPerPage),
        });

      const facetsHiddenByViewport = isMobile
        ? facetsHiddenMobile
        : facetsHidden;
      const facetsHiddenProcessed = isAnixeTourism
        ? facetsHiddenForAnixeTourism
        : facetsHiddenByViewport;
      const sortOptions = isAnixeTourism
        ? sortCategoryAnixeOptions.map((option) =>
            option === originalSortOptions.finalPrice
              ? { ...option, default: true }
              : option
          )
        : sortCategoryOptions;
      const rowClasses = isMobile ? classesMobile.row : classes.row;

      const productsTypes =
        products?.msProducts &&
        isArrayHasItems(products.msProducts.edges) &&
        products.msProducts.edges.map(
          (product: { node: IProduct }) => product?.node?.mb_product_type
        );
      const productsTypesFiltered =
        isArrayHasItems(productsTypes) &&
        productsTypes.filter(
          (item) =>
            item === "international_tourism_other" ||
            item === "international_tourism_travelplanet"
        );

      const isInternationalTourismOnly =
        productsTypes?.length === (productsTypesFiltered as unknown[])?.length;

      return (
        <>
          {isAnixeTourism && isMobile && !locationParams ? (
            <div>
              <CampaignGraphicsProvider placement={placements.tourismBanner}>
                <DomesticTourismForm
                  variant={DOMESTIC_TOURISM_FORM_VARIANTS.LISTING}
                  fromFilters
                />
              </CampaignGraphicsProvider>
            </div>
          ) : null}
          <ErrorBoundary>
            <ListingHeader
              name={name}
              count={
                isArrayHasItems(subscriptionItems) ? idsMapped.length : count
              }
              // @ts-ignore
              sortOptions={sortOptions}
              isAnixeTourism={isAnixeTourism}
              // @ts-ignore
              stats={stats}
              facets={facets}
              facetsHidden={facetsHiddenProcessed}
              attributes={data}
            />
          </ErrorBoundary>
          {isAnixeTourism && isMobile && isArrayHasItems(ids) && (
            <Map variant="secondary" ids={ids} />
          )}
          <div className={`row ${rowClasses}`}>
            {!isMobile && (
              <aside className="col col-3">
                <ErrorBoundary>
                  <Filters
                    stats={stats}
                    facets={facets}
                    facetsHidden={facetsHiddenProcessed}
                    attributes={data}
                    isAnixeTourism={isAnixeTourism}
                    isSubscriptions={isArrayHasItems(filteredSubSkuArray)}
                    isInternationalTourismOnly={isInternationalTourismOnly}
                    withShiftedActiveFilters={!isAnixeTourism}
                    ids={idsMapped}
                  />
                </ErrorBoundary>
              </aside>
            )}
            <div className="col col-9">
              <ErrorBoundary>
                {(!!productsLoading && idsMemo.length <= gridPerPage) ||
                (!products?.length && !ids) ||
                isLoadingFilteredSub ? (
                  <SkeletonProductsList />
                ) : (
                  <ProductsWrapper
                    ids={idsMapped}
                    isAnixeTourism={!!isAnixeTourism}
                    additionalParameters={additionalParameters}
                    categoriesPath={categoriesPath}
                    cgGroup1Path={cgGroup1Path}
                    dataFilteredBenefits={dataFiltered}
                    idsValidSubscriptions={idsValidSubscriptions}
                  />
                )}
              </ErrorBoundary>
            </div>
          </div>
        </>
      );
    }
  }
};

interface IProductsWrapperProps {
  ids: number[];
  categoriesPath?: string;
  cgGroup1Path?: string;
  isAnixeTourism: boolean;
  additionalParameters: string;
  dataFilteredBenefits: ISubscriptionFilterBenefitGroups;
  idsValidSubscriptions?: number[];
}

const ProductsWrapper: FC<IProductsWrapperProps> = ({
  ids,
  dataFilteredBenefits,
  isAnixeTourism,
  additionalParameters,
  categoriesPath,
  cgGroup1Path,
  idsValidSubscriptions = [],
}) => {
  const { isMobile } = useAppContext();

  if (
    dataFilteredBenefits?.benefitGroups &&
    Object.keys(dataFilteredBenefits?.benefitGroups).length > 0
  ) {
    // filter out all subscription items on mobile
    const validIds = isMobile
      ? [...ids.filter((el) => !idsValidSubscriptions?.includes(el))]
      : ids;
    return (
      <Products
        ids={validIds}
        isAnixeTourism={false}
        additionalParameters={additionalParameters}
        categoriesPath={categoriesPath}
        cgGroup1Path={cgGroup1Path}
      />
    );
  }

  return (
    <Products
      ids={ids}
      isAnixeTourism={isAnixeTourism}
      additionalParameters={additionalParameters}
      categoriesPath={categoriesPath}
      cgGroup1Path={cgGroup1Path}
    />
  );
};

interface IGetProductsWithTypeArgs {
  attributes: IAttribute[];
  productTypeFromFilter: string;
  ids: number[];
}

const getProductsWithType = ({
  attributes,
  productTypeFromFilter,
  ids,
}: IGetProductsWithTypeArgs) => {
  if (!productTypeFromFilter) {
    return ids;
  }

  const mbProductTypeAttribute = attributes.find(
    (item) => item.attribute_code === "mb_product_type_filter"
  );

  const value = mbProductTypeAttribute?.attribute_options?.find(
    ({ value }) => value === productTypeFromFilter
  );

  return ids.map((id) => ({ id: id.toString(), mb_product_type: value?.code }));
};

export default ListingSearch;
